// src/pages/admin/ImagesAdmin.jsx
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVehiclesWithImageCount } from "../../api/cars";
import {
  getImages,
  uploadImages,
  deleteImage,
  deleteBulkImages,
  fetchImagesFromAzure,
} from "../../api/images";
import {
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  CloudArrowDownIcon,
} from "@heroicons/react/24/outline";
import ConfirmModal from "../../components/ConfirmModal";
import { toast } from "react-toastify";
import useDebounce from "../../stores/useDebounce";
import LoadingSpinner from "../../components/LoadingSpinner";

import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/outline";
import { setMainImage, reorderImages } from "../../api/images";

export default function ImagesAdmin() {
  const qc = useQueryClient();

  // ─── UI STATE ────────────────────────────────────────────────────────────
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [files, setFiles] = useState([]);
  const [deleteIds, setDeleteIds] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ─── VEHICLES PAGINATION ─────────────────────────────────────────────────
  const [vehiclesPage, setVehiclesPage] = useState(1);
  // const vehiclesLimit = 10;

  // ─── SEARCH STATE ─────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [limit, setLimit] = useState(10);

  // whenever you flip the vehicles page, clear selection
  useEffect(() => {
    setSelectedVehicle(null);
    setDeleteIds([]);
  }, [vehiclesPage]);

  // ─── IMAGES RESET ON VEHICLE CHANGE ──────────────────────────────────────
  useEffect(() => {
    // reset any image‐page or deletes if you had them:
    setDeleteIds([]);
  }, [selectedVehicle]);

  // ─── 1️⃣ FETCH PAGINATED VEHICLES + IMAGECOUNT ─────────────────────────

  const { data: resp = {}, isLoading: carsLoading } = useQuery({
    queryKey: [
      "vehiclesWithCount",
      vehiclesPage,
      limit,
      debouncedSearch.trim(),
    ],
    queryFn: () =>
      getVehiclesWithImageCount({
        page: vehiclesPage,
        limit,
        // assumes your API helper supports a "search" param:
        q: debouncedSearch.trim() || undefined,
      }),
    keepPreviousData: true,
  });

  const vehicles = resp.data || [];
  const vehiclesPages = resp.totalPages || 1;

  // ─── 2️⃣ FETCH IMAGES FOR SELECTED VEHICLE ──────────────────────────────
  const { data: images = [], isLoading: imagesLoading } = useQuery({
    queryKey: ["imagesByVehicle", selectedVehicle?.id],
    queryFn: () => getImages({ vehicleId: selectedVehicle?.id }),
    enabled: Boolean(selectedVehicle?.id),
  });

  const fetchAzureMutation = useMutation({
    mutationFn: ({ vehicleId, registration }) =>
      fetchImagesFromAzure(vehicleId, registration),
    onSuccess: (data) => {
      toast.success(data.message || "✅ Images fetched from Azure");
      qc.invalidateQueries(["imagesByVehicle", selectedVehicle?.id]);
      qc.invalidateQueries(["vehiclesWithCount"]);
    },
    onError: (err) =>
      toast.error(
        err.response?.data?.message || "❌ Failed to fetch from Azure"
      ),
  });

  // ─── 3️⃣ UPLOAD MUTATION ────────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: () => uploadImages(selectedVehicle?.id, files),
    onSuccess: () => {
      qc.invalidateQueries(["imagesByVehicle", selectedVehicle?.id]);
      setFiles([]);
      toast.success("✔️ Images uploaded");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "❌ Upload failed"),
  });

  // ─── 4️⃣ DELETE ONE IMAGE ───────────────────────────────────────────────
  const deleteOne = useMutation({
    mutationFn: (id) => deleteImage(id),
    onSuccess: () => {
      qc.invalidateQueries(["imagesByVehicle", selectedVehicle?.id]);
      setDeleteIds([]);
      setConfirmOpen(false);
      toast.success("🗑️ Image deleted");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "❌ Delete failed"),
  });

  // ─── 5️⃣ BULK DELETE ────────────────────────────────────────────────────
  const deleteBulk = useMutation({
    mutationFn: (ids) => deleteBulkImages(ids),
    onSuccess: () => {
      qc.invalidateQueries(["imagesByVehicle", selectedVehicle?.id]);
      setDeleteIds([]);
      setConfirmOpen(false);
      toast.success(`🗑️ Deleted ${deleteIds.length} image(s)`);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "❌ Bulk delete failed"),
  });

  // ─── HANDLERS ───────────────────────────────────────────────────────────
  const handleUpload = () => {
    if (!selectedVehicle) {
      toast.warn("Please select a vehicle first");
      return;
    }
    if (!files.length) {
      toast.warn("Please choose at least one file");
      return;
    }
    uploadMutation.mutate();
  };

  const handleConfirm = () => {
    if (deleteIds.length === 1) deleteOne.mutate(deleteIds[0]);
    else deleteBulk.mutate(deleteIds);
  };

  // set main —
  const setMainMutation = useMutation({
    mutationFn: ({ vehicleId, imageId }) => setMainImage(vehicleId, imageId),
    onSuccess: () => {
      qc.invalidateQueries(["imagesByVehicle", selectedVehicle.id]);
      toast.success("⭐ Main image set");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "❌ Couldn’t set main"),
  });

  // reorder —
  const reorderMutation = useMutation({
    mutationFn: (orders) => reorderImages(orders),
    onSuccess: () => {
      qc.invalidateQueries(["imagesByVehicle", selectedVehicle.id]);
      toast.success("🔀 Order updated");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "❌ Couldn’t reorder"),
  });

  const handleSetMain = (imageId) => {
    setMainMutation.mutate({ vehicleId: selectedVehicle.id, imageId });
  };

  const moveImage = (fromIdx, toIdx) => {
    const items = [...images];
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    const orders = items.map((img, idx) => ({
      id: img._id,
      position: idx,
    }));
    reorderMutation.mutate(orders);
  };

  // if (carsLoading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold">Manage Vehicle Images</h2>

      <div className="flex justify-between items-center">
        {/* ─── Search Bar ─────────────────────────────────────────────────── */}
        <div className="flex items-center mb-4">
          <input
            type="text"
            placeholder="Search reg number…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setVehiclesPage(1); // reset back to page 1 on new search
            }}
            className="
               w-fit
              border border-surface
              text-primary placeholder-secondary-accent
              focus:ring-primary focus:border-accent
              rounded px-3 py-1
            "
          />
        </div>

        {/* ─── Page-Size Selector ─────────────────────────────────────────────── */}
        <div className="flex justify-end items-center mb-4">
          <label className="mr-2 text-sm">Rows per page:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(+e.target.value);
              setVehiclesPage(1); // reset to page 1
            }}
            className="text-primary py-1 border rounded"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ─── Image Detail Panel ───────────────────────────────────────────── */}
      {selectedVehicle && (
        <div className="border bg-darksec p-4 rounded space-y-4">
          <h3 className="text-xl">
            Images for {selectedVehicle.registeration} — {selectedVehicle.make}{" "}
            {selectedVehicle.model}
          </h3>

          {/* Uploader */}
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-3">
              {/* Upload Button */}
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const all = Array.from(e.target.files);
                    const images = all.filter((f) =>
                      f.type.startsWith("image/")
                    );
                    const skipped = all.length - images.length;

                    if (skipped > 0) {
                      toast.warn(
                        `${skipped} non-image file${
                          skipped > 1 ? "s were" : " was"
                        } skipped`
                      );
                    }
                    setFiles(images);
                  }}
                  className="border px-2 py-1 rounded"
                />

                <button
                  onClick={handleUpload}
                  disabled={!files.length || uploadMutation.isPending}
                  className={`flex items-center px-4 py-2 rounded text-darksec bg-surface
                ${
                  uploadMutation.isPending
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-primary/90"
                }`}
                >
                  {uploadMutation.isPending ? (
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <PlusIcon className="h-5 w-5 mr-2" />
                  )}
                  {uploadMutation.isPending ? "Uploading…" : "Upload"}
                </button>
              </div>

              {/* Fetch images from Azure  */}
              {/* <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    fetchAzureMutation.mutate({
                      vehicleId: selectedVehicle.id,
                      registration: selectedVehicle.registration,
                    })
                  }
                  disabled={fetchAzureMutation.isPending}
                  className={`flex items-center px-4 py-2 rounded text-darksec bg-accent hover:bg-accent/90 ${
                    fetchAzureMutation.isPending
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {fetchAzureMutation.isPending ? (
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <CloudArrowDownIcon className="h-5 w-5 mr-2" />
                  )}
                  {fetchAzureMutation.isPending
                    ? "Fetching…"
                    : "Fetch from Azure"}
                </button>
              </div> */}
            </div>

            <p className="text-xs text-secondaryAccent">
              Only images are accepted.
            </p>
          </div>

          {files.length > 0 && (
            <div className="mt-2 text-sm text-secondaryAccent">
              {files.length} file{files.length > 1 ? "s" : ""} selected:
              <ul className="list-disc list-inside ml-4">
                {files.map((f) => (
                  <li key={f.name} className="truncate">
                    {f.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Images Grid */}
          {imagesLoading ? (
            <p>Loading images…</p>
          ) : !images.length ? (
            <p>No images for this vehicle</p>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {images.map((img, idx) => (
                <div
                  key={img._id}
                  className="relative border rounded overflow-hidden"
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-24 object-cover"
                  />

                  {/* star = main selector */}
                  {/* star */}
                  <button
                    onClick={() => handleSetMain(img._id)}
                    disabled={setMainMutation.isLoading}
                    className="absolute top-2 left-2 p-1 bg-white rounded-full
             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {setMainMutation.isLoading &&
                    setMainMutation.variables?.imageId === img._id ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    ) : img.isPrimary ? (
                      <StarSolid className="h-5 w-5 text-yellow-400" />
                    ) : (
                      <StarOutline className="h-5 w-5 text-gray-500" />
                    )}
                  </button>

                  {/* up/down reorder */}
                  <div className="absolute bottom-2 left-2 flex space-x-1">
                    {/* up/down */}
                    <button
                      onClick={() => moveImage(idx, idx - 1)}
                      disabled={idx === 0 || reorderMutation.isLoading}
                      className="p-1 bg-white rounded
             disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {reorderMutation.isLoading ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowUpIcon className="h-4 w-4 text-primary" />
                      )}
                    </button>
                    {/* <button
                      onClick={() => moveImage(idx, idx - 1)}
                      disabled={idx === 0}
                      className="p-1 bg-white rounded"
                    >
                      <ArrowUpIcon className="h-4 w-4 text-primary" />
                    </button>
                    <button
                      onClick={() => moveImage(idx, idx + 1)}
                      disabled={idx === images.length - 1}
                      className="p-1 bg-white rounded"
                    >
                      <ArrowDownIcon className="h-4 w-4 text-primary" />
                    </button> */}
                  </div>

                  {/* existing delete checkbox */}
                  <input
                    type="checkbox"
                    checked={deleteIds.includes(img._id)}
                    onChange={() =>
                      setDeleteIds((ids) =>
                        ids.includes(img._id)
                          ? ids.filter((i) => i !== img._id)
                          : [...ids, img._id]
                      )
                    }
                    className="absolute top-2 right-2 bg-white rounded"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Bulk Delete */}
          {deleteIds.length > 0 && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="mt-2 px-4 py-2 bg-accent text-darksec rounded hover:bg-danger/90"
            >
              <TrashIcon className="h-5 w-5 inline mr-1" />
              Delete {deleteIds.length} Selected
            </button>
          )}
        </div>
      )}

      {/* ─── Vehicles Table ───────────────────────────────────────────────── */}
      <div className="overflow-x-auto bg-surface rounded-lg shadow py-5 mb-6">
        {carsLoading ? (
          <>
            <LoadingSpinner />
          </>
        ) : vehicles.length === 0 ? (
          <p className="p-4 text-center">No cars found</p>
        ) : (
          <table className="w-full table-auto">
            <thead className="bg-surface">
              <tr>
                <th className="p-2 text-left">Reg number</th>
                <th className="p-2 text-left">Make</th>
                <th className="p-2 text-left">Model</th>
                <th className="p-2 text-left">Color</th>
                <th className="p-2 text-center"># Images</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => {
                return (
                  <tr
                    key={v.id}
                    onClick={() => {
                      setSelectedVehicle(v);
                      setDeleteIds([]);
                    }}
                    className={`
              cursor-pointer  
              transition-all duration-150
              ${
                selectedVehicle?.id === v.id
                  ? "bg-primary/10 border-2 border-accent rounded"
                  : "hover:bg-lightsec dark:hover:bg-darksec"
              }
             `}
                  >
                    <td className="p-2">{v.registration}</td>
                    <td className="p-2">{v.make || "—"}</td>
                    <td className="p-2">{v.model || "—"}</td>
                    <td className="p-2">{v.color}</td>
                    <td className="p-2 text-center">{v.imageCount}</td>
                    <td className="p-2 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedVehicle(v);
                          setDeleteIds([]);
                        }}
                        className="text-accent hover:underline"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Vehicles Pagination ──────────────────────────────────────────── */}
      {vehiclesPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-4">
          <button
            onClick={() => setVehiclesPage((p) => Math.max(1, p - 1))}
            disabled={vehiclesPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {vehiclesPage} of {vehiclesPages}
          </span>
          <button
            onClick={() =>
              setVehiclesPage((p) => Math.min(vehiclesPages, p + 1))
            }
            disabled={vehiclesPage === vehiclesPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* ─── Confirm Modal ─────────────────────────────────────────────────── */}
      <ConfirmModal
        isOpen={confirmOpen}
        title="Confirm Delete"
        message={`Delete ${deleteIds.length} image(s)?`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        isLoading={deleteOne.isPending || deleteBulk.isPending}
      />
    </div>
  );
}
